import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories, categoryList } from "@/mocks/categories";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || "hot";
  const activeCategory = categories.find((c) => c.id === activeSlug) || categories[0];

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${activeCategory.name} - 蘑菇视频`,
    description: activeCategory.description,
    url: `https://www.moguvideo.com/category/${activeSlug}`,
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - 蘑菇视频分类浏览`}
        description={`蘑菇视频${activeCategory.name}：${activeCategory.description}。高清流畅播放，支持安卓苹果iPad全平台。`}
        keywords={`${activeCategory.name},蘑菇视频,视频播放器,APP下载,高清视频`}
        canonicalUrl={`https://www.moguvideo.com/category/${activeSlug}`}
        schemaData={categorySchema}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: "分类浏览" }, { label: activeCategory.name }]} />

        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                {activeCategory.name}
              </h1>
              <p className="text-sm text-foreground-600">{activeCategory.description}</p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 flex-wrap">
              {categoryList.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.id
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Category Items Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {activeCategory.items.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/article-${item.id.split("-")[1]}`}
                  className="bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/50 transition-colors cursor-pointer group"
                >
                  <div className="aspect-[2/3] overflow-hidden relative">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    {item.tag && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                        {item.tag}
                      </span>
                    )}
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}